import React, { useEffect, useState } from "react";
import { Search, Loader2, User, IdCard, Users, Printer, CheckCircle2 } from "lucide-react";
import api, { formatApiError, money, openPdf, clientLabel } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { NumberInput } from "./NumberInput";
import { ItemsEditor } from "./ItemsEditor";
import { SearchableSelect } from "./SearchableSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const emptyForm = {
  buyer_mode: "cf", client_id: "", client_name: "", client_nit: "", client_address: "CIUDAD",
  currency: "GTQ", condition: "contado", credit_days: 0, notes: "", as_cambiaria: false, items: [],
};

const MODES = [
  { key: "cf", label: "Consumidor Final", icon: User },
  { key: "id", label: "Por NIT / CUI", icon: IdCard },
  { key: "client", label: "Cliente existente", icon: Users },
];

export const QuickInvoiceDialog = ({ open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [clients, setClients] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setCreated(null);
      api.get("/clients?selectable=true").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [open]);

  const subtotal = form.items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100), 0);
  const askClose = () => form.items.length === 0 || window.confirm("Tienes ítems sin guardar. ¿Seguro que deseas cerrar? Se perderá lo capturado.");
  const cfOverLimit = form.buyer_mode === "cf" && form.currency === "GTQ" && subtotal >= 2500;

  const setMode = (m) => setForm((f) => ({ ...emptyForm, items: f.items, currency: f.currency, buyer_mode: m }));

  const lookupNit = async () => {
    const nit = (form.client_nit || "").trim();
    if (!nit) return toast.error("Ingresa un NIT o CUI primero");
    setLookingUp(true);
    try {
      const r = await api.get(`/fel/lookup-nit?nit=${encodeURIComponent(nit)}`);
      setForm((f) => ({ ...f, client_name: r.data.name || f.client_name, client_address: r.data.fiscal_address || f.client_address }));
      toast.success(`Receptor: ${r.data.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se encontró el NIT/CUI"); }
    finally { setLookingUp(false); }
  };

  const selectClient = (client_id) => {
    const c = clients.find((x) => x.id === client_id);
    setForm((f) => ({ ...f, client_id, client_name: c?.business_name || "", client_nit: c?.nit || "", client_address: c?.fiscal_address || "CIUDAD",
      condition: Number(c?.credit_days || 0) > 0 ? "credito" : "contado", credit_days: Number(c?.credit_days || 0) }));
  };

  const submit = async (certify) => {
    if (form.items.length === 0) return toast.error("Agrega al menos una línea");
    if (form.buyer_mode === "client" && !form.client_id) return toast.error("Selecciona un cliente");
    if (form.buyer_mode === "id" && !(form.client_nit || "").trim()) return toast.error("Ingresa el NIT o CUI");
    if (form.buyer_mode === "id" && !(form.client_name || "").trim()) return toast.error("Ingresa el nombre del receptor (usa Buscar)");
    setSaving(true);
    try {
      const payload = { ...form, certify, as_cambiaria: form.condition === "credito" && form.as_cambiaria };
      const r = await api.post("/invoices/direct", payload);
      toast.success(certify ? `Factura ${r.data.number} creada y certificada` : `Factura ${r.data.number} creada`);
      setCreated(r.data);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const printCreated = () => {
    const path = created.fel_authorization ? `/invoices/${created.id}/fel-pdf` : `/invoices/${created.id}/pdf`;
    openPdf(path).catch(() => toast.error("No se pudo generar el PDF"));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (askClose()) onOpenChange(false); } else onOpenChange(true); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quick-invoice-dialog"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Factura Rápida</DialogTitle>
          <DialogDescription>Factura directamente sin crear un pedido. Ideal para CF, NIT o CUI/DPI.</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4 py-2" data-testid="qi-success">
            <div className="rounded-sm border border-green-200 bg-green-50 p-5 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Factura creada</p>
              <p className="font-heading text-2xl font-bold text-green-700" data-testid="qi-created-number">{created.number}</p>
              <p className="text-xs text-muted-foreground mt-1">{created.client_name} · {money(created.currency, created.total)}{created.fel_authorization ? " · Certificada FEL ✓" : " · sin certificar"}</p>
            </div>
            <Button className="w-full rounded-sm gap-2 font-bold" onClick={printCreated} data-testid="qi-view-pdf"><Printer className="h-4 w-4" /> Ver / Imprimir PDF</Button>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-sm" onClick={() => { setCreated(null); setForm(emptyForm); }} data-testid="qi-new">Nueva factura</Button>
              <Button variant="ghost" className="flex-1 rounded-sm" onClick={() => { if (askClose()) onOpenChange(false); }} data-testid="qi-close">Cerrar</Button>
            </div>
          </div>
        ) : (<>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Receptor</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = form.buyer_mode === m.key;
                return (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`qi-mode-${m.key}`}
                    className={`flex items-center justify-center gap-2 rounded-sm border px-3 py-2 text-sm font-semibold transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                    <Icon className="h-4 w-4" /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {form.buyer_mode === "cf" && (
            <div className="space-y-2">
              <div className="rounded-sm border border-border bg-muted/40 p-3 text-sm">
                Se facturará a <strong>Consumidor Final</strong> (NIT: CF). Puedes escribir el nombre del cliente (opcional).
                {cfOverLimit && <span className="block mt-1 text-amber-600 font-semibold">⚠ El SAT no permite CF con total ≥ Q2,500. Usa NIT o CUI.</span>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-widest">Nombre del cliente (opcional)</Label>
                <Input className="rounded-sm" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="qi-cf-name" placeholder="CONSUMIDOR FINAL" />
              </div>
            </div>
          )}

          {form.buyer_mode === "id" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-widest">NIT / CUI (DPI 13 díg.)</Label>
                <div className="flex gap-2">
                  <Input className="rounded-sm" value={form.client_nit} onChange={(e) => setForm((f) => ({ ...f, client_nit: e.target.value }))} data-testid="qi-nit" placeholder="Ej. 112939422" />
                  <Button type="button" variant="outline" className="rounded-sm shrink-0" onClick={lookupNit} disabled={lookingUp} data-testid="qi-nit-lookup" title="Traer datos del SAT">{lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-widest">Nombre del receptor</Label>
                <Input className="rounded-sm" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="qi-name" placeholder="Nombre / Razón social" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-widest">Dirección</Label>
                <Input className="rounded-sm" value={form.client_address} onChange={(e) => setForm((f) => ({ ...f, client_address: e.target.value }))} data-testid="qi-address" placeholder="CIUDAD" />
              </div>
            </div>
          )}

          {form.buyer_mode === "client" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Cliente *</Label>
              <SearchableSelect value={form.client_id} onChange={selectClient} testid="qi-client-select"
                placeholder="Selecciona un cliente" searchPlaceholder="Escribe para buscar cliente..."
                options={clients.map((c) => ({ value: c.id, label: clientLabel(c) }))} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Moneda</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="qi-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="GTQ">GTQ (Quetzales)</SelectItem><SelectItem value="USD">USD (Dólares)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Condición</Label>
              <Select value={form.condition} onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="qi-condition"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="contado">Contado</SelectItem><SelectItem value="credito">Crédito</SelectItem></SelectContent>
              </Select>
            </div>
            {form.condition === "credito" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-widest">Días de crédito</Label>
                <NumberInput className="rounded-sm" value={form.credit_days} onValueChange={(v) => setForm((f) => ({ ...f, credit_days: v }))} data-testid="qi-credit-days" />
              </div>
            )}
          </div>

          {form.condition === "credito" && (
            <label className="flex items-center gap-2 text-sm rounded-sm border border-border p-2.5 cursor-pointer" data-testid="qi-cambiaria-wrap">
              <input type="checkbox" checked={form.as_cambiaria} onChange={(e) => setForm((f) => ({ ...f, as_cambiaria: e.target.checked }))} data-testid="qi-cambiaria" />
              <span>Certificar como <strong>Factura Cambiaria (FCAM)</strong> (crédito)</span>
            </label>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Ítems</Label>
            <div className="mt-1.5">
              <ItemsEditor items={form.items} onChange={(items) => setForm((f) => ({ ...f, items }))} currency={form.currency}
                priceKey="unit_price" priceLabel="Precio" showDiscount showObservation testidPrefix="qi"
                productMap={(p) => ({ product_id: p.id, code: p.code, description: p.description, unit: p.unit || "Unidad", unit_price: p.price || 0 })} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex-1"><Textarea placeholder="Observaciones" className="rounded-sm" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            <div className="w-full sm:w-64 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono" data-testid="qi-subtotal">{money(form.currency, subtotal)}</span></div>
              <div className="text-xs text-muted-foreground italic">Precios con IVA incluido</div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-primary"><span>TOTAL</span><span className="font-mono" data-testid="qi-total">{money(form.currency, subtotal)}</span></div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => { if (askClose()) onOpenChange(false); }} disabled={saving}>Cancelar</Button>
          <Button variant="outline" className="rounded-sm font-bold" onClick={() => submit(false)} disabled={saving || cfOverLimit} data-testid="qi-save">Guardar</Button>
          <Button className="rounded-sm font-bold bg-green-700 hover:bg-green-800" onClick={() => submit(true)} disabled={saving || cfOverLimit} data-testid="qi-save-certify">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar y Certificar (FEL)"}</Button>
        </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
